/**
 * Canvas surfaces for the renderer: canvas factory, image/bitmap texture caches (with LRU pixel
 * budgets), colour-transformed ("tinted") copies and a pool of offscreen layers.
 *
 * Works with DOM canvases, OffscreenCanvas and @napi-rs/canvas (Node tests): only the portable
 * Canvas 2D API subset is used (drawImage, getImageData/putImageData/createImageData).
 */
import type { DecodedImage } from '../platform/types';
import { CT, ColorStages, applyColorToRGBA, ctKey, stagesKey } from './math';

/** Minimal canvas interface (HTMLCanvasElement, OffscreenCanvas, @napi-rs/canvas Canvas). */
export interface CanvasLike {
  width: number;
  height: number;
  getContext(type: '2d', options?: any): any;
}

export type CanvasFactory = (width: number, height: number) => CanvasLike;

/** Default factory: OffscreenCanvas, else a DOM <canvas>. */
export function defaultCanvasFactory(width: number, height: number): CanvasLike {
  const g: any = globalThis as any;
  if (typeof g.OffscreenCanvas === 'function') return new g.OffscreenCanvas(width, height);
  if (g.document) {
    const c = g.document.createElement('canvas');
    c.width = width;
    c.height = height;
    return c;
  }
  throw new Error('No canvas implementation available: pass `canvasFactory` to the renderer');
}

/** A canvas plus its 2D context. */
export interface Surface {
  canvas: CanvasLike;
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
}

function makeSurface(factory: CanvasFactory, w: number, h: number, readback: boolean): Surface {
  const canvas = factory(Math.max(1, w), Math.max(1, h));
  const ctx = canvas.getContext('2d', readback ? { willReadFrequently: true } : undefined) as CanvasRenderingContext2D;
  if (!ctx) throw new Error('2D context unavailable');
  return { canvas, ctx, width: canvas.width, height: canvas.height };
}

interface CacheEntry {
  surface: Surface;
  pixels: number;
  lastFrame: number;
}

/** LRU cache of surfaces with a pixel budget; entries used in the current frame are never evicted. */
class SurfaceLRU<K> {
  private readonly map = new Map<K, CacheEntry>();
  private pixels = 0;
  constructor(private budget: number) {}

  get(key: K, frame: number): Surface | null {
    const e = this.map.get(key);
    if (!e) return null;
    e.lastFrame = frame;
    this.map.delete(key);
    this.map.set(key, e);
    return e.surface;
  }

  set(key: K, surface: Surface, frame: number): void {
    const old = this.map.get(key);
    if (old) {
      this.pixels -= old.pixels;
      this.map.delete(key);
    }
    const px = surface.width * surface.height;
    this.map.set(key, { surface, pixels: px, lastFrame: frame });
    this.pixels += px;
    this.evict(frame);
  }

  evict(frame: number): void {
    if (this.pixels <= this.budget) return;
    for (const [k, e] of this.map) {
      if (this.pixels <= this.budget) break;
      if (e.lastFrame === frame) continue;
      this.map.delete(k);
      this.pixels -= e.pixels;
      // Release backing memory eagerly (canvas GC can be lazy).
      e.surface.canvas.width = 1;
      e.surface.canvas.height = 1;
    }
  }

  clear(): void {
    this.map.clear();
    this.pixels = 0;
  }

  /** Drop one entry and release its backing store. */
  delete(key: K): void {
    const e = this.map.get(key);
    if (!e) return;
    this.map.delete(key);
    this.pixels -= e.pixels;
    e.surface.canvas.width = 1;
    e.surface.canvas.height = 1;
  }

  get size(): number {
    return this.map.size;
  }
  get pixelCount(): number {
    return this.pixels;
  }
}

interface BitmapTexture {
  surface: Surface;
  version: number;
  w: number;
  h: number;
  /** Regions uploaded since the prescaled (sharp-bilinear) copies were last refreshed: [x0, y0, x1, y1] each. */
  prescaleDirty?: Array<[number, number, number, number]> | null;
}

interface SharpTile {
  /** Core source rows drawn from this tile. */
  y0: number;
  y1: number;
  /** Source rows held by the surface (core plus a 1-row margin for seamless bilinear sampling). */
  ty0: number;
  ty1: number;
  surface: Surface | null;
  /** Frame from which the tile may be drawn (the frame after it was built). */
  readyFrame: number;
}

interface SharpBitmap {
  f: number;
  w: number;
  h: number;
  version: number;
  tiles: SharpTile[];
}

interface DeviceTile {
  /** Device rows [y0, y1) of the cached image held by this tile. */
  y0: number;
  y1: number;
  surface: Surface | null;
  readyFrame: number;
}

interface DeviceBitmap {
  sx: number;
  sy: number;
  f: number;
  w: number;
  h: number;
  dw: number;
  dh: number;
  version: number;
  tiles: DeviceTile[];
  /** Frame of the last change that invalidated (almost) the whole cache. */
  fullChangeFrame: number;
  /** Frame the cache was created in (its bitmap is usually uploaded in the same frame: no tile build then). */
  createdFrame: number;
}

/** Destination pixels per prescaled tile of a BitmapData (one tile is built per frame). */
const SHARP_TILE_PX = 1024 * 1024;

/** Largest device-resolution BitmapData cache (pixels): a 1612x650 terrain fits up to 3.6 device px per texel. */
const MAX_DEVICE_BITMAP_PX = 16 * 1024 * 1024;
/** Smaller on-screen bitmaps are resampled every frame (cheap) instead of cached at device resolution. */
const MIN_DEVICE_BITMAP_PX = 64 * 1024;
/** Pixel changes up to this many device pixels are re-rendered in the same frame; larger ones rebuild tiles over frames. */
const DEVICE_SYNC_UPDATE_PX = 1536 * 1024;

/** Largest prescaled BitmapData copy (pixels); bigger factors are reduced (a 1612x650 terrain gets at most 3x). */
const MAX_PRESCALED_BITMAP_PX = 12 * 1024 * 1024;

function unionRect(a: [number, number, number, number] | null | undefined, b: [number, number, number, number]): [number, number, number, number] {
  return a ? [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[2], b[2]), Math.max(a[3], b[3])] : b;
}

export interface SurfaceOptions {
  /** Pixel budget for decoded-image canvases (atlases); default 96M px. */
  imageBudget?: number;
  /** Pixel budget for colour-transformed copies; default 16M px. */
  tintBudget?: number;
  /** Pixel budget for sharp-bilinear prescaled copies of raw texel regions; default 48M px. */
  prescaleBudget?: number;
  /** Max idle layers kept in the pool (default 24). */
  maxPooledLayers?: number;
  /**
   * Draw shape images from small per-ImageRef canvases (sub-rectangle + 1px margin, cached like
   * colour-transformed copies) instead of whole atlas canvases. Browsers do not need it;
   * @napi-rs/canvas copies the whole source canvas on every drawImage (a 2048x2048 atlas: ~9 ms per
   * draw), so Node renderers enable it through `napiCanvasOptions()` (render/napi.ts).
   */
  subImages?: boolean;
}

/**
 * Surface manager. `frame` must be advanced by the renderer (beginFrame) so LRU eviction never
 * drops a texture used in the frame being drawn.
 */
export class Surfaces {
  private frame = 0;
  private readonly images: SurfaceLRU<DecodedImage>;
  private readonly tints: SurfaceLRU<string>;
  /** Sharp-bilinear integer prescales of raw (nearest-sampled) texel regions, see prescaled(). */
  private readonly prescales: SurfaceLRU<string>;
  private bitmaps = new WeakMap<object, BitmapTexture>();
  private sharpBitmaps = new WeakMap<object, SharpBitmap>();
  private deviceBitmaps = new WeakMap<object, DeviceBitmap>();
  /** BitmapData rewritten as a whole every few frames (animations): a device-resolution cache would only churn. */
  private volatileBitmaps = new WeakSet<object>();
  /**
   * Scratch canvases for the integer nearest prescale that device-resolution tiles are resampled from: one for tile
   * builds and one for pixel-change updates, each filled at most once per frame (overwriting a canvas that a pending
   * draw still reads forces a GPU sync or copy: ~2 ms per reuse in Chromium). Tile builds alternate between two
   * canvases because a built tile is first drawn in the next frame, which keeps its draw from the scratch pending.
   */
  private deviceScratch: Record<'build0' | 'build1' | 'dirty', Surface | null> = { build0: null, build1: null, dirty: null };
  private buildFlip = 0;
  /** Frame of the last device tile build: at most one per frame across all bitmaps (spreads scene-start cost). */
  private deviceBuildFrame = -1;
  private readonly tintIds = new WeakMap<object, number>();
  /** Per BitmapData: colour key -> tint-cache key of the latest pixel version (older versions are dropped). */
  private bitmapTintKeys = new WeakMap<object, Map<string, string>>();
  private nextTintId = 1;
  private readonly opaque = new WeakMap<DecodedImage, Map<string, boolean>>();
  private readonly pool: Surface[] = [];
  private readonly maxPooled: number;
  /** See SurfaceOptions.subImages. */
  readonly subImages: boolean;
  /** Statistics for tests/debugging. */
  readonly stats = { imageUploads: 0, bitmapUploads: 0, bitmapPartialUploads: 0, tintBuilds: 0, layersCreated: 0, prescaleBuilds: 0, prescaleUpdates: 0 };

  constructor(
    readonly factory: CanvasFactory = defaultCanvasFactory,
    opts: SurfaceOptions = {},
  ) {
    this.images = new SurfaceLRU(opts.imageBudget ?? 96 * 1024 * 1024);
    this.tints = new SurfaceLRU(opts.tintBudget ?? 16 * 1024 * 1024);
    this.prescales = new SurfaceLRU(opts.prescaleBudget ?? 48 * 1024 * 1024);
    this.maxPooled = opts.maxPooledLayers ?? 24;
    this.subImages = !!opts.subImages;
  }

  /**
   * The last frame in which a cache began to change what unchanged pixels look like (a sharp or device-resolution
   * tile built, to be drawn from the next frame on, or tiles dropped to be rebuilt): a damage renderer draws the next
   * frame whole (src/flash/render/damage.ts).
   */
  unstableFrame = -Infinity;

  /** Whether a cache changed unchanged pixels in the last frame drawn (see unstableFrame). */
  get unstable(): boolean {
    return this.unstableFrame >= this.frame;
  }

  beginFrame(): void {
    this.frame++;
  }

  endFrame(): void {
    this.images.evict(this.frame);
    this.tints.evict(this.frame);
    this.prescales.evict(this.frame);
  }

  private id(o: object): number {
    let id = this.tintIds.get(o);
    if (!id) this.tintIds.set(o, (id = this.nextTintId++));
    return id;
  }

  /** Canvas holding a decoded image (host `source` when provided, e.g. an ImageBitmap). */
  image(img: DecodedImage): CanvasImageSource {
    if (img.source) return img.source as CanvasImageSource;
    let s = this.images.get(img, this.frame);
    if (!s) {
      s = makeSurface(this.factory, img.width, img.height, false);
      const id = s.ctx.createImageData(img.width, img.height);
      id.data.set(img.data);
      s.ctx.putImageData(id, 0, 0);
      this.images.set(img, s, this.frame);
      this.stats.imageUploads++;
    }
    return s.canvas as unknown as CanvasImageSource;
  }

  /**
   * Colour-transformed copy of the sub-rectangle (sx, sy, sw, sh) of a decoded image: `ct`, then the
   * outer colour stages (distributed colour-matrix filters), computed from the exact straight RGBA.
   * The copy keeps a 1px margin of the surrounding source pixels (atlas padding), so filtered
   * sampling at the sub-rectangle edges sees the same neighbourhood as a draw from the atlas; draw
   * the returned `source` with the source rectangle (x, y, sw, sh).
   */
  tintedImage(
    img: DecodedImage,
    sx: number,
    sy: number,
    sw: number,
    sh: number,
    ct: Readonly<CT>,
    outer: ColorStages | null = null,
  ): { source: CanvasImageSource; x: number; y: number } {
    const x0 = Math.max(0, sx - 1);
    const y0 = Math.max(0, sy - 1);
    const x1 = Math.min(img.width, sx + sw + 1);
    const y1 = Math.min(img.height, sy + sh + 1);
    const key = `i${this.id(img)}|${sx},${sy},${sw},${sh}|${ctKey(ct)}|${stagesKey(outer)}`;
    let s = this.tints.get(key, this.frame);
    if (!s) {
      const cw = Math.max(1, x1 - x0);
      const ch = Math.max(1, y1 - y0);
      s = makeSurface(this.factory, cw, ch, false);
      let id: ImageData;
      if (img.source) {
        // Natively decoded image (ImageBitmap): read back only this sub-rectangle instead of materializing the
        // whole atlas page (a 2048x2048 readback per page caused long frames when tinted UI first appeared).
        s.ctx.drawImage(img.source as any, x0, y0, cw, ch, 0, 0, cw, ch);
        id = s.ctx.getImageData(0, 0, cw, ch);
      } else {
        id = s.ctx.createImageData(cw, ch);
        const W = img.width;
        for (let y = 0; y < y1 - y0; y++) {
          const src = ((y0 + y) * W + x0) * 4;
          id.data.set(img.data.subarray(src, src + (x1 - x0) * 4), y * cw * 4);
        }
      }
      const d = id.data;
      applyColorToRGBA(d, ct, outer, true);
      s.ctx.putImageData(id, 0, 0);
      this.tints.set(key, s, this.frame);
      this.stats.tintBuilds++;
    }
    return { source: s.canvas as unknown as CanvasImageSource, x: sx - x0, y: sy - y0 };
  }

  /**
   * Canvas mirroring a BitmapData (premultiplied Uint32 pixels): full upload on first use or size
   * change, then only the dirty region reported by `$takeDirty()` whenever `$version` changed.
   */
  bitmap(bd: any): CanvasImageSource {
    const w = bd.$width as number;
    const h = bd.$height as number;
    let t = this.bitmaps.get(bd);
    if (!t || t.w !== w || t.h !== h) {
      const surface = t && t.surface.width >= w && t.surface.height >= h ? t.surface : makeSurface(this.factory, w, h, false);
      if (surface.width !== w || surface.height !== h) {
        surface.canvas.width = w;
        surface.canvas.height = h;
        surface.width = w;
        surface.height = h;
      }
      t = { surface, version: -1, w, h, prescaleDirty: [[0, 0, w, h]] };
      this.bitmaps.set(bd, t);
      bd.$takeDirty();
      this.upload(bd, t, 0, 0, w, h);
      this.stats.bitmapUploads++;
    } else if (t.version !== bd.$version) {
      // Separate rectangles: two distant craters in one frame upload two small regions, not their union.
      const rects = (typeof bd.$takeDirtyRects === 'function' ? bd.$takeDirtyRects() : ((d) => (d ? [d] : null))(bd.$takeDirty())) as Array<[number, number, number, number]> | null;
      if (rects) {
        for (const d of rects) {
          const x0 = Math.max(0, Math.min(w, d[0]));
          const y0 = Math.max(0, Math.min(h, d[1]));
          const x1 = Math.max(x0, Math.min(w, d[2]));
          const y1 = Math.max(y0, Math.min(h, d[3]));
          if (x1 > x0 && y1 > y0) {
            this.upload(bd, t, x0, y0, x1, y1);
            (t.prescaleDirty ??= []).push([x0, y0, x1, y1]);
          }
        }
        this.stats.bitmapPartialUploads++;
      } else {
        // Dirty region consumed elsewhere (another renderer): re-upload everything.
        this.upload(bd, t, 0, 0, w, h);
        t.prescaleDirty = [[0, 0, w, h]];
        this.stats.bitmapUploads++;
      }
    }
    t.version = bd.$version;
    return t.surface.canvas as unknown as CanvasImageSource;
  }

  /**
   * "Sharp bilinear" for raw texels that Flash samples with nearest neighbour (non-smoothed bitmap fills, Bitmaps
   * with smoothing=false): a cached copy of the source region upscaled by the integer factor `f` with nearest
   * sampling, which the caller then draws with bilinear smoothing at the remaining (<= 1) scale. Result: equal-size
   * texels with a 1-device-pixel transition, instead of uneven blocky texels (nearest at 1.33x/1.8x) or a blurry
   * bilinear upscale. Built once per (source, region, factor) and cached (LRU); nothing is resampled per frame.
   * The copy keeps a 1-texel margin of the source around the region so edge sampling matches the unscaled draw.
   */
  prescaled(src: CanvasImageSource, sx: number, sy: number, sw: number, sh: number, f: number): { source: CanvasImageSource; x: number; y: number } {
    const s: any = src;
    const x0 = Math.max(0, sx - 1);
    const y0 = Math.max(0, sy - 1);
    const x1 = Math.min(s.width, sx + sw + 1);
    const y1 = Math.min(s.height, sy + sh + 1);
    const key = `p${this.id(s)}|${sx},${sy},${sw},${sh}|${f}`;
    let p = this.prescales.get(key, this.frame);
    if (!p) {
      const cw = Math.max(1, x1 - x0);
      const ch = Math.max(1, y1 - y0);
      p = makeSurface(this.factory, cw * f, ch * f, false);
      const c: any = p.ctx;
      c.imageSmoothingEnabled = false;
      c.drawImage(src, x0, y0, cw, ch, 0, 0, cw * f, ch * f);
      this.prescales.set(key, p, this.frame);
      this.stats.prescaleBuilds++;
    }
    return { source: p.canvas as unknown as CanvasImageSource, x: (sx - x0) * f, y: (sy - y0) * f };
  }

  /** Largest usable prescale factor (<= f) for a BitmapData of w x h pixels. */
  bitmapPrescaleFactor(w: number, h: number, f: number): number {
    while (f > 1 && w * h * f * f > MAX_PRESCALED_BITMAP_PX) f--;
    return f;
  }

  /**
   * Draws a BitmapData with sharp bilinear (see prescaled()) into `ctx` using its current transform: an integer
   * nearest prescale kept in horizontal tiles of ~1M px. Tiles are built one per frame and first drawn on the next
   * frame (a single multi-megapixel canvas made the browser's first use of it a 40+ ms frame); rows whose tile is
   * not ready yet are drawn with plain nearest sampling, like before. Pixel changes (terrain destruction) update
   * only the affected rectangle in the affected tiles.
   */
  drawSharpBitmap(ctx: any, bd: any, f: number): void {
    this.dropDeviceBitmap(bd);
    const base = this.bitmap(bd);
    const t = this.bitmaps.get(bd)!;
    const w = t.w;
    const h = t.h;
    let e = this.sharpBitmaps.get(bd);
    if (!e || e.f !== f || e.w !== w || e.h !== h) {
      if (e) for (const tile of e.tiles) if (tile.surface) (tile.surface.canvas.width = 1), (tile.surface.canvas.height = 1);
      const rows = Math.max(8, Math.floor(SHARP_TILE_PX / Math.max(1, w * f * f)));
      const tiles: SharpTile[] = [];
      for (let y = 0; y < h; y += rows) {
        const y1 = Math.min(h, y + rows);
        tiles.push({ y0: y, y1, ty0: Math.max(0, y - 1), ty1: Math.min(h, y1 + 1), surface: null, readyFrame: Infinity });
      }
      e = { f, w, h, version: bd.$version, tiles };
      this.sharpBitmaps.set(bd, e);
      t.prescaleDirty = null;
      this.stats.prescaleBuilds++;
    }
    const copy = (tile: SharpTile, x0: number, y0: number, x1: number, y1: number) => {
      const c: any = tile.surface!.ctx;
      const oy = tile.ty0;
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.globalAlpha = 1;
      c.globalCompositeOperation = 'source-over';
      c.imageSmoothingEnabled = false;
      c.clearRect(x0 * f, (y0 - oy) * f, (x1 - x0) * f, (y1 - y0) * f);
      c.drawImage(base, x0, y0, x1 - x0, y1 - y0, x0 * f, (y0 - oy) * f, (x1 - x0) * f, (y1 - y0) * f);
    };
    // Pixel changes: refresh the changed rectangle in every built tile that holds it (margins included).
    const d = t.prescaleDirty;
    if (d || e.version !== bd.$version) {
      for (const r of d ?? [[0, 0, w, h]])
        for (const tile of e.tiles) {
          if (!tile.surface) continue;
          const y0 = Math.max(r[1], tile.ty0);
          const y1 = Math.min(r[3], tile.ty1);
          if (y1 > y0 && r[2] > r[0]) copy(tile, r[0], y0, r[2], y1);
        }
      if (d) this.stats.prescaleUpdates++;
      t.prescaleDirty = null;
      e.version = bd.$version;
    }
    // Build at most one tile per frame.
    const next = e.tiles.find((tile) => !tile.surface);
    if (next) {
      this.unstableFrame = this.frame;
      next.surface = makeSurface(this.factory, w * f, (next.ty1 - next.ty0) * f, false);
      copy(next, 0, next.ty0, w, next.ty1);
      next.readyFrame = this.frame + 1;
    }
    for (const tile of e.tiles) {
      const rows = tile.y1 - tile.y0;
      if (tile.surface && this.frame >= tile.readyFrame) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'low';
        ctx.drawImage(tile.surface.canvas, 0, (tile.y0 - tile.ty0) * f, w * f, rows * f, 0, tile.y0, w, rows);
      } else {
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(base, 0, tile.y0, w, rows, 0, tile.y0, w, rows);
      }
    }
  }

  /**
   * Whether a w x h BitmapData drawn at sx x sy device px per texel should use the device-resolution cache: it is
   * big enough on screen for per-frame resampling to matter, fits the budget and is not rewritten every few frames.
   */
  deviceBitmapCacheable(bd: any, w: number, h: number, sx: number, sy: number): boolean {
    const px = w * h * sx * sy;
    return px >= MIN_DEVICE_BITMAP_PX && px <= MAX_DEVICE_BITMAP_PX && !this.volatileBitmaps.has(bd);
  }

  /**
   * Sharp bilinear for a BitmapData (the terrain, water and HUD bitmaps) drawn with an axis-aligned transform `m`
   * ([sx, 0, 0, sy, tx, ty], sx/sy > 1): the result of sharp bilinear (integer nearest prescale by `f`, then
   * bilinear) is cached at device resolution and each frame is a 1:1 blit at the rounded device position.
   * Resampling a 1080p terrain every frame cost ~4-5 ms on software-rasterized canvases; the blit costs less than the
   * original nearest draw. The cache is rebuilt only when the scale changes (resize, dynamic resolution), in
   * ~1M px tiles built one per frame (rows of unbuilt tiles are drawn with plain nearest sampling meanwhile);
   * pixel changes (terrain destruction) re-render only the changed rectangle; bitmaps rewritten as a whole every
   * few frames fall back to per-frame resampling (drawSharpBitmap). The world geometry is unchanged:
   * the position is rounded by at most half a device pixel.
   */
  drawDeviceBitmap(ctx: any, bd: any, m: ArrayLike<number>, f: number): void {
    const base = this.bitmap(bd);
    const t = this.bitmaps.get(bd)!;
    const w = t.w;
    const h = t.h;
    const sx = m[0];
    const sy = m[3];
    let e = this.deviceBitmaps.get(bd);
    if (!e || e.w !== w || e.h !== h || e.f !== f || Math.abs(e.sx - sx) > 1e-6 || Math.abs(e.sy - sy) > 1e-6) {
      this.dropDeviceBitmap(bd);
      this.dropSharpBitmap(bd);
      const dw = Math.ceil(w * sx);
      const dh = Math.ceil(h * sy);
      const rows = Math.max(8, Math.floor(SHARP_TILE_PX / Math.max(1, dw)));
      const tiles: DeviceTile[] = [];
      for (let y = 0; y < dh; y += rows) tiles.push({ y0: y, y1: Math.min(dh, y + rows), surface: null, readyFrame: Infinity });
      e = { sx, sy, f, w, h, dw, dh, version: bd.$version, tiles, fullChangeFrame: -Infinity, createdFrame: this.frame };
      this.deviceBitmaps.set(bd, e);
      t.prescaleDirty = null;
      this.stats.prescaleBuilds++;
    }
    // Pixel changes: re-render the changed rectangle (+ the bilinear footprint) in the built tiles it touches.
    const d = t.prescaleDirty;
    if (d || e.version !== bd.$version) {
      const rects = (d ?? [[0, 0, w, h]]).filter((r) => r[2] > r[0] && r[3] > r[1]);
      // Most of the bitmap rewritten twice within 60 frames: an animation, resampled per frame from now on.
      if (rects.reduce((a, r) => a + (r[2] - r[0]) * (r[3] - r[1]), 0) * 4 > w * h) {
        if (this.frame - e.fullChangeFrame < 60) this.volatileBitmaps.add(bd);
        e.fullChangeFrame = this.frame;
      }
      const regions = rects.map((r): [number, number, number, number] => [
        Math.max(0, Math.floor(r[0] * sx) - 2),
        Math.max(0, Math.floor(r[1] * sy) - 2),
        Math.min(e.dw, Math.ceil(r[2] * sx) + 2),
        Math.min(e.dh, Math.ceil(r[3] * sy) + 2),
      ]);
      if (regions.reduce((a, g) => a + (g[2] - g[0]) * (g[3] - g[1]), 0) <= DEVICE_SYNC_UPDATE_PX) {
        // Craters: redrawn now (no stale frame), all regions from one scratch fill.
        const built = e.tiles.filter((tile) => tile.surface && tile.readyFrame !== Infinity);
        if (built.length && regions.length) this.renderDeviceRegions(e, base, built, regions, 'dirty');
      } else {
        for (const g of regions) for (const tile of e.tiles) if (tile.y0 < g[3] && tile.y1 > g[1]) tile.readyFrame = Infinity; // rebuild over frames
        this.unstableFrame = this.frame;
      }
      if (d) this.stats.prescaleUpdates++;
      t.prescaleDirty = null;
      e.version = bd.$version;
    }
    // Build (or rebuild) at most one tile per frame, not in the frame the cache was created; it is drawn from the
    // next frame on.
    const next = this.frame > e.createdFrame && this.deviceBuildFrame !== this.frame ? e.tiles.find((tile) => tile.readyFrame === Infinity) : undefined;
    if (next) {
      this.unstableFrame = this.frame;
      this.deviceBuildFrame = this.frame;
      if (!next.surface) next.surface = makeSurface(this.factory, e.dw, next.y1 - next.y0, false);
      this.renderDeviceRegions(e, base, [next], [[0, next.y0, e.dw, next.y1]], (this.buildFlip ^= 1) ? 'build1' : 'build0');
      next.readyFrame = this.frame + 1;
      // Cache complete: the build scratch canvases (~1M px each) are not kept around.
      if (!e.tiles.some((tile) => tile.readyFrame === Infinity))
        for (const k of ['build0', 'build1'] as const) {
          const sc = this.deviceScratch[k];
          if (sc) (sc.canvas.width = 1), (sc.canvas.height = 1);
          this.deviceScratch[k] = null;
        }
    }
    // Rounded device extents [round(x0), round(x0 + w * sx)): bitmaps placed edge to edge (backgrounds made of
    // pieces) meet exactly, without a gap or an overlap column, whatever the fractional camera position.
    const ox = Math.round(m[4]);
    const oy = Math.round(m[5]);
    const W = Math.min(e.dw, Math.round(m[4] + w * sx) - ox);
    const H = Math.min(e.dh, Math.round(m[5] + h * sy) - oy);
    for (const tile of e.tiles) {
      if (tile.surface && this.frame >= tile.readyFrame) {
        const rows = Math.min(tile.y1, H) - tile.y0;
        if (rows <= 0 || W <= 0) continue;
        ctx.setTransform(1, 0, 0, 1, ox, oy);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(tile.surface.canvas, 0, 0, W, rows, 0, tile.y0, W, rows);
      } else {
        const ty0 = Math.floor(tile.y0 / sy);
        const ty1 = Math.min(h, Math.ceil(tile.y1 / sy));
        ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
        ctx.imageSmoothingEnabled = false;
        if (ty1 > ty0) ctx.drawImage(base, 0, ty0, w, ty1 - ty0, 0, ty0, w, ty1 - ty0);
      }
    }
    ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
  }

  /**
   * Renders device rects [X0, Y0, X1, Y1) (cache coordinates) with sharp bilinear into the parts of `tiles` they
   * cover. The source texels of all rects are prescaled into one fill of the `kind` scratch canvas, stacked
   * vertically with a transparent gap, before any of them is read.
   */
  private renderDeviceRegions(e: DeviceBitmap, base: CanvasImageSource, tiles: DeviceTile[], regions: Array<[number, number, number, number]>, kind: 'build0' | 'build1' | 'dirty'): void {
    const { sx, sy, f } = e;
    const GAP = 2;
    // Source texels covering each rect plus one texel of bilinear footprint on each side, and their scratch rows.
    // At the bitmap's right/bottom edge one replicated texel column/row is appended, so the cache is opaque up to its
    // last (partially covered) device column/row instead of leaving it empty (clamp-to-edge, no seams).
    const parts: Array<{ g: [number, number, number, number]; tx0: number; ty0: number; tw: number; th: number; ec: number; er: number; oy: number }> = [];
    let pw = 0;
    let ph = 0;
    for (const g of regions) {
      const tx0 = Math.max(0, Math.floor(g[0] / sx) - 1);
      const tx1 = Math.min(e.w, Math.ceil(g[2] / sx) + 1);
      const ty0 = Math.max(0, Math.floor(g[1] / sy) - 1);
      const ty1 = Math.min(e.h, Math.ceil(g[3] / sy) + 1);
      if (tx1 <= tx0 || ty1 <= ty0) continue;
      const ec = tx1 === e.w ? 1 : 0;
      const er = ty1 === e.h ? 1 : 0;
      parts.push({ g, tx0, ty0, tw: tx1 - tx0, th: ty1 - ty0, ec, er, oy: ph });
      pw = Math.max(pw, (tx1 - tx0 + ec) * f);
      ph += (ty1 - ty0 + er) * f + GAP;
    }
    if (!parts.length) return;
    let sc = this.deviceScratch[kind];
    if (!sc || sc.width < pw + GAP || sc.height < ph) {
      const cw = Math.max(pw + GAP, sc?.width ?? 0);
      const ch = Math.max(ph, sc?.height ?? 0);
      if (sc) (sc.canvas.width = 1), (sc.canvas.height = 1);
      sc = this.deviceScratch[kind] = makeSurface(this.factory, cw, ch, false);
    }
    const p: any = sc.ctx;
    p.setTransform(1, 0, 0, 1, 0, 0);
    p.globalAlpha = 1;
    p.globalCompositeOperation = 'source-over';
    p.clearRect(0, 0, pw + GAP, ph); // no stale pixels next to a region for bilinear taps at the bitmap edges
    p.imageSmoothingEnabled = false;
    for (const q of parts) {
      p.drawImage(base, q.tx0, q.ty0, q.tw, q.th, 0, q.oy, q.tw * f, q.th * f);
      if (q.ec) p.drawImage(base, e.w - 1, q.ty0, 1, q.th, q.tw * f, q.oy, f, q.th * f);
      if (q.er) p.drawImage(base, q.tx0, e.h - 1, q.tw, 1, 0, q.oy + q.th * f, q.tw * f, f);
      if (q.ec && q.er) p.drawImage(base, e.w - 1, e.h - 1, 1, 1, q.tw * f, q.oy + q.th * f, f, f);
    }
    for (const q of parts) {
      const [X0, Y0, X1, Y1] = q.g;
      for (const tile of tiles) {
        const y0 = Math.max(Y0, tile.y0);
        const y1 = Math.min(Y1, tile.y1);
        if (y1 <= y0) continue;
        const c: any = tile.surface!.ctx;
        c.setTransform(1, 0, 0, 1, 0, 0);
        c.globalAlpha = 1;
        c.globalCompositeOperation = 'source-over';
        const full = X0 === 0 && X1 === e.dw && y0 === tile.y0 && y1 === tile.y1;
        if (!full) {
          c.save();
          c.beginPath();
          c.rect(X0, y0 - tile.y0, X1 - X0, y1 - y0);
          c.clip();
        }
        c.clearRect(X0, y0 - tile.y0, X1 - X0, y1 - y0);
        // Same mapping as drawing the whole prescaled bitmap at (sx, sy) from the origin: prescaled px -> px * s / f.
        c.setTransform(sx / f, 0, 0, sy / f, q.tx0 * sx, q.ty0 * sy - tile.y0);
        c.imageSmoothingEnabled = true;
        c.imageSmoothingQuality = 'low';
        const qw = (q.tw + q.ec) * f;
        const qh = (q.th + q.er) * f;
        c.drawImage(sc.canvas, 0, q.oy, qw, qh, 0, 0, qw, qh);
        if (!full) c.restore();
        else c.setTransform(1, 0, 0, 1, 0, 0);
      }
    }
    // A rare large update (many craters at once) does not pin a multi-megapixel scratch canvas.
    if (kind === 'dirty' && sc.width * sc.height > 1024 * 1024) {
      sc.canvas.width = 1;
      sc.canvas.height = 1;
      this.deviceScratch.dirty = null;
    }
  }

  private dropDeviceBitmap(bd: any): void {
    const e = this.deviceBitmaps.get(bd);
    if (!e) return;
    for (const tile of e.tiles) if (tile.surface) (tile.surface.canvas.width = 1), (tile.surface.canvas.height = 1);
    this.deviceBitmaps.delete(bd);
  }

  private dropSharpBitmap(bd: any): void {
    const e = this.sharpBitmaps.get(bd);
    if (!e) return;
    for (const tile of e.tiles) if (tile.surface) (tile.surface.canvas.width = 1), (tile.surface.canvas.height = 1);
    this.sharpBitmaps.delete(bd);
  }

  private upload(bd: any, t: BitmapTexture, x0: number, y0: number, x1: number, y1: number): void {
    const ctx = t.surface.ctx;
    const id = ctx.createImageData(x1 - x0, y1 - y0);
    bd.toRGBA(id.data, x0, y0, x1, y1);
    ctx.putImageData(id, x0, y0);
  }

  /** Colour-transformed copy of a BitmapData (all pixels, including transparent ones: Flash applies offsets to the whole bitmap). */
  tintedBitmap(bd: any, ct: Readonly<CT>, outer: ColorStages | null = null): CanvasImageSource {
    const colorKey = `${ctKey(ct)}|${stagesKey(outer)}`;
    const key = `b${this.id(bd)}v${bd.$version}|${colorKey}`;
    let s = this.tints.get(key, this.frame);
    if (!s) {
      let keys = this.bitmapTintKeys.get(bd);
      if (!keys) this.bitmapTintKeys.set(bd, (keys = new Map()));
      const prev = keys.get(colorKey);
      if (prev !== undefined && prev !== key) this.tints.delete(prev);
      keys.set(colorKey, key);
      const w = bd.$width as number;
      const h = bd.$height as number;
      s = makeSurface(this.factory, w, h, false);
      const id = s.ctx.createImageData(w, h);
      bd.toRGBA(id.data, 0, 0, w, h);
      applyColorToRGBA(id.data, ct, outer, false);
      s.ctx.putImageData(id, 0, 0);
      this.tints.set(key, s, this.frame);
      this.stats.tintBuilds++;
    }
    return s.canvas as unknown as CanvasImageSource;
  }

  /** True when every pixel of the sub-rectangle is fully opaque (mask fast path: clip instead of layer). */
  isOpaque(img: DecodedImage, sx: number, sy: number, sw: number, sh: number): boolean {
    let m = this.opaque.get(img);
    if (!m) this.opaque.set(img, (m = new Map()));
    const key = `${sx},${sy},${sw},${sh}`;
    let v = m.get(key);
    if (v === undefined) {
      v = sw > 0 && sh > 0;
      const W = img.width;
      const d = img.data;
      outer: for (let y = sy; y < sy + sh; y++) {
        for (let x = sx, i = (y * W + sx) * 4 + 3; x < sx + sw; x++, i += 4) {
          if (d[i] !== 255) {
            v = false;
            break outer;
          }
        }
      }
      m.set(key, v);
    }
    return v;
  }

  /** A new standalone surface (not pooled; owned by the caller). */
  createSurface(w: number, h: number, readback: boolean): Surface {
    this.stats.layersCreated++;
    return makeSurface(this.factory, w, h, readback);
  }

  /** Offscreen layer of at least w x h (cleared in [0, w) x [0, h), identity transform). */
  acquireLayer(w: number, h: number): Surface {
    let best = -1;
    for (let i = 0; i < this.pool.length; i++) {
      const s = this.pool[i];
      if (s.width >= w && s.height >= h && s.width * s.height <= Math.max(4 * w * h, 64 * 64)) {
        if (best < 0 || s.width * s.height < this.pool[best].width * this.pool[best].height) best = i;
      }
    }
    let s: Surface;
    if (best >= 0) s = this.pool.splice(best, 1)[0];
    else {
      s = makeSurface(this.factory, Math.ceil(w / 32) * 32 || 32, Math.ceil(h / 32) * 32 || 32, true);
      this.stats.layersCreated++;
    }
    const ctx = s.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, w, h);
    return s;
  }

  releaseLayer(s: Surface): void {
    if (this.pool.length < this.maxPooled) this.pool.push(s);
    else {
      s.canvas.width = 1;
      s.canvas.height = 1;
    }
  }

  /** Drop all cached textures (e.g. after a context loss): everything is re-uploaded on next use. */
  clear(): void {
    this.images.clear();
    this.tints.clear();
    this.bitmaps = new WeakMap();
    this.bitmapTintKeys = new WeakMap();
    this.pool.length = 0;
  }

  get cachedImageCount(): number {
    return this.images.size;
  }
  get cachedTintCount(): number {
    return this.tints.size;
  }
}
